import * as React from 'react';
import Checkbox from "components/UI/Checkbox";
import FlexboxContainerComponent from "components/UI/FlexboxContainer";
import SelectComponent from "components/UI/Select";
import { renderToast, toast } from "components/UI/ToastNotification";
import Brand from "data/Brand";
import User, { isVendor } from "data/User";
import { Color, FontWeight, Percent } from "lib/styles";
import styled, { css } from "styled-components";
import { Main } from "styles/themeColors";
import { FontSize } from "styles/themeSizes";
import { BRAND_CONSTANTS } from 'constants/brands';
import { PermissionCode } from 'data/UserPermissions';
import { useReadPermission } from 'hooks/useUserRoles';

const Heading = styled.h1`
  font-size: ${FontSize.large};
  font-weight: ${FontWeight.medium};
`;

const Label = styled.label<any>`
  font-size: 15px;
  line-height: 1.47;
  color: ${Color.dimGray};

  ${({ margin }) => margin && css`
    margin: ${margin};
  `}

  ${({ maxWidth }) => maxWidth && css`
    max-width: ${maxWidth};
  `}

  ${({ italic }) => italic && css`
    font-style: italic;
  `}
`;

const LinkedBrandsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const BrandLabel = styled.label`
  border: 1px solid ${Main.lightGray};
  color: ${Main.brandDark};
  padding: 5px;
  text-decoration: underline;
  border-radius: 10px;
`;

interface BrandTypeSelectionProps {
    brand: Brand;
    setBrand: (brand: Brand) => void;
    user: User | null;
    brandsList: Brand[];
    hasEditRights?: boolean;

}

const BrandTypeSelection = (props: BrandTypeSelectionProps) => {

    const { brand, setBrand, user, brandsList, hasEditRights } = props;
    const { readOnly: isAddDetailsReadOnly } = useReadPermission(PermissionCode.BRANDS_ADD_DETAILS);

    return (
        <FlexboxContainerComponent bgColor={Color.lightGray} flexDirection='column' padding='10px 24px'>
            <Heading>{BRAND_CONSTANTS.BRAND_TYPE_CONFIGURATION}</Heading>
            <Label maxWidth={Percent.half}>
                {BRAND_CONSTANTS.CUSTOMER_FACING_DESCRIPTION}
            </Label>

            <FlexboxContainerComponent width='100%' margin='10px 0 10px 0'>
                <Checkbox
                    id='brand-type-customer-facing'
                    appearance="none"
                    className='align-checkbox'
                    checkboxSize="s"
                    checked={brand.isParentBrand}
                    label={BRAND_CONSTANTS.MARK_PARENT_BRAND}
                    disabled={!hasEditRights}
                    onChange={val => {
                        // Show error if brand is part of another parent brand
                        if (brand.parentId) renderToast(toast.TYPE.ERROR, BRAND_CONSTANTS.BRAND_CANNOT_BE_ADDED);
                        else {
                            const newState = { ...brand, isParentBrand: val };
                            if (!val) newState.selectedBrandsIds = [];
                            setBrand(newState);
                        }
                    }}
                    theme={brand.isParentBrand ? 'brandLight' : 'lightGray'}
                    bgColor={brand.isParentBrand ? Main.brandLight : Main.accent}
                    permissionCode={PermissionCode.BRANDS_ADD_DETAILS}
                />
            </FlexboxContainerComponent>


            {
                brand.isParentBrand && (
                    <SelectComponent
                        label={BRAND_CONSTANTS.ADD_BRANDS}
                        labelPosition="top"
                        options={brandsList?.filter(b => b.id !== brand.id).map((b: Brand) => ({
                            value: b.name,
                            id: b.id + '',
                            disabled: (!!b.parentId && b.parentId !== brand.id) || b.isParentBrand
                        }))}
                        onChange={(event: any) => {
                            const selBrands = event.target.value;
                            const selectedItem = event.target.selected;

                            if (selectedItem) {
                                const selectedBrandsIds = selBrands.map((brandName: string) => {
                                    return brandsList.find((b: Brand) => b.name === brandName)?.id;
                                });

                                setBrand({ ...brand, selectedBrandsIds: selectedBrandsIds });
                            }
                        }
                        }
                        value={
                            brand.selectedBrandsIds
                                ? brandsList.filter((b: Brand) => brand.selectedBrandsIds?.includes(b.id)).map((b: Brand) => b.name)
                                : []
                        }
                        readOnly={isAddDetailsReadOnly}
                        defaultValue={BRAND_CONSTANTS.SELECT_BRANDS}
                        multiple
                        width='50%'
                        limitTags={3}
                        isOptional
                    />
                )
            }

        </FlexboxContainerComponent>
    );
}

export default BrandTypeSelection;